import React, { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { MuiThemeProvider, Button, Collapse, Typography } from '@material-ui/core';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import classNames from 'classnames';
import ReactGA from 'react-ga4';
import { BASE_URL } from '../../../constants/paths.constants';
import { axuminTheme, poslumaTheme, buttonText, darkGrey, universalStyles, white } from '../../../app/theme';
import CASE_STUDY, { CaseStudyFormat } from '../../../constants/caseStudy.constants';
import CaseStudy from './CaseStudy';

const useStyles = makeStyles(() => ({
  ...universalStyles,
  row: {
    backgroundColor: white,
    padding: '5px 20px',
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    boxSizing: 'border-box',
  },
  headshotImage: {
    width: 111,
    height: 'auto',
    paddingRight: 20,
  },
  text: {
    marginBottom: 4,
  },
  rightSideHeader: {
    paddingRight: 25,
  },
  enterButton: {
    minWidth: 132,
    fontSize: 16,
    fontWeight: 600,
    borderRadius: 24,
    color: buttonText,
  },
  openBox: {
    position: 'relative',
  },
  closeButton: {
    position: 'absolute',
    top: '0.5em',
    right: '0.5em',
    color: darkGrey,
  },
}));

type CaseStudyRowProps = {
  trainingId: number;
  module: string;
  page: number;
  index: number;
  setNextDisabledCS?: (disabled: boolean) => void;
};

const CaseStudyRow = ({ trainingId, module, page, index, setNextDisabledCS }: CaseStudyRowProps): JSX.Element => {
  const classes = useStyles();
  const theme = trainingId === 1 ? axuminTheme : poslumaTheme;
  const caseStudyData = (CASE_STUDY[module][page.toString()] as CaseStudyFormat[]) || [];
  const { headshot, title } = caseStudyData[index];
  const [isOpen, setIsOpen] = useState(false);
  const handleClickedOpen = () => {
    ReactGA.event({
      category: 'event',
      action: `${trainingId === 2 ? 'POSLUMA ' : ''}Addnl Case Study ${(index + 1).toString()} Open`,
    });
    setIsOpen(true);
  };

  return (
    <MuiThemeProvider theme={theme}>
      <Collapse in={!isOpen} timeout="auto" unmountOnExit>
        <div className={classes.row}>
          <img className={classes.headshotImage} src={`${BASE_URL}${headshot.path}`} alt={`${headshot.alt}`} />
          <div className={classes.rightSideHeader}>
            <Typography variant="h4" className={classes.text}>
              {title || 'Case Study'}
            </Typography>
            <Typography variant="body1">
              <b>Disclaimer: </b>
              These case studies are provided as examples of the interpretation and utility of
              {trainingId === 1 ? ' Axumin' : ' POSLUMA '}
              {trainingId === 1 ? (
                <>
                  <sup>®</sup>
                </>
              ) : (
                ''
              )}
              {trainingId === 1
                ? ' (fluciclovine F 18) PET/CT imaging results. Axumin '
                : ' PET imaging results. POSLUMA '}
              is not indicated for directing or changing patient management. Such decisions must be based on the
              independent medical judgment of the physician(s) responsible for the individual patient’s management
              {module === 'additional' ? '. Does not include all clinical information needed to assess the case.' : '.'}
              {module === 'poslumaAdditional'
                ? ' Does not include all clinical information needed to assess the case.'
                : '.'}
            </Typography>
            <Typography variant="body1">
              <i>Photo is not of an actual patient.</i>
            </Typography>
          </div>
          <Button variant="contained" color="primary" className={classes.enterButton} onClick={handleClickedOpen}>
            Open
          </Button>
        </div>
      </Collapse>
      <Collapse in={isOpen} timeout="auto" unmountOnExit>
        <div className={classNames(classes.whiteBox, classes.openBox)}>
          <IconButton aria-label="close" className={classes.closeButton} onClick={() => setIsOpen(false)}>
            <CloseIcon />
          </IconButton>
          <CaseStudy
            trainingId={trainingId}
            page={page}
            module={module}
            index={index}
            setNextDisabledCS={setNextDisabledCS}
          />
        </div>
      </Collapse>
    </MuiThemeProvider>
  );
};

export default CaseStudyRow;
